<template>
  <div class="sal-portal-container">
    <div class="version-ctrl-panel-container">
      <div id="sal-version" class="version">{{ version }}</div>
      <dat-gui id="control-panel" close-text="Close controls" open-text="Open controls" close-position="bottom"
        :closed="true">
        <dat-select v-model="pdfPrintConfig.pdfOrientation" :items="pdfOrientations" label="orientation" />
        <dat-select v-model="pdfPrintConfig.pdfFormat" :items="pdfFormats" label="PDF Format" />
        <button @click="clearAll" :disabled="loadedComponents.length === 0" class="clear-button">
          Clear All
        </button>
      </dat-gui>
    </div>

    <!-- Drag and Drop Layout -->
    <div class="drag-drop-layout">
      <!-- Sidebar with draggable components -->
      <div class="sidebar" :class="{ 'collapsed': isSidebarCollapsed }">
        <div class="sidebar-header">
          <h3 v-if="!isSidebarCollapsed">Library</h3>
          <button @click="toggleSidebar" class="sidebar-toggle">
            {{ isSidebarCollapsed ? '▶️' : '◀️' }}
          </button>
        </div>
        
        <div v-if="!isSidebarCollapsed" class="sidebar-content">
          <!-- Components Group -->
          <div class="sidebar-group">
            <div class="group-header" @click="toggleGroup('components')">
              <span class="group-icon">{{ groupStates.components ? '📂' : '📁' }}</span>
              <h4>Components</h4>
              <span class="toggle-icon">{{ groupStates.components ? '▼' : '▶' }}</span>
            </div>
            <div v-if="groupStates.components" class="group-content">
              <div 
                v-for="component in availableComponentsList" 
                :key="component"
                class="draggable-item"
                draggable="true"
                @dragstart="onDragStart($event, component, 'component')"
                @dragend="onDragEnd"
              >
                <div class="item-icon">📊</div>
                <div class="item-name">{{ formatComponentName(component) }}</div>
              </div>
            </div>
          </div>

          <!-- Layouts Group -->
          <div class="sidebar-group">
            <div class="group-header" @click="toggleGroup('layouts')">
              <span class="group-icon">{{ groupStates.layouts ? '📂' : '📁' }}</span>
              <h4>Layouts</h4>
              <span class="toggle-icon">{{ groupStates.layouts ? '▼' : '▶' }}</span>
            </div>
            <div v-if="groupStates.layouts" class="group-content">
              <div 
                v-for="layout in availableLayoutsList" 
                :key="layout.id"
                class="draggable-item layout-item"
                draggable="true"
                @dragstart="onDragStart($event, layout, 'layout')"
                @dragend="onDragEnd"
              >
                <div class="item-icon">{{ layout.icon }}</div>
                <div class="item-name">{{ layout.name }}</div>
                <div class="item-description">{{ layout.description }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Drop area -->
      <div class="main-area">
        <div 
          class="drop-zone"
          :class="{ 'drag-over': isDragOver, 'has-components': loadedComponents.length > 0 }"
          @drop="onDrop"
          @dragover="onDragOver"
          @dragenter="onDragEnter"
          @dragleave="onDragLeave"
        >
          <!-- Layout Sections Display -->
          <div v-if="currentLayout" class="layout-container">
            <div class="layout-header">
              <h3>{{ currentLayout.name }} Layout</h3>
              <button @click="clearLayout" class="clear-layout-btn">Clear Layout</button>
            </div>
            <div class="sections-container" :class="currentLayout.id">
              <div 
                v-for="section in layoutSections" 
                :key="section.id"
                class="layout-section"
                :class="{ 'drag-over': section.isDragOver }"
                :style="{ width: section.width }"
                @drop="onSectionDrop($event, section.id)"
                @dragover="onSectionDragOver($event, section.id)"
                @dragenter="onSectionDragEnter($event, section.id)"
                @dragleave="onSectionDragLeave($event, section.id)"
              >
                <div class="section-header">
                  <h4>{{ section.name }}</h4>
                  <span class="component-count">{{ section.components.length }} components</span>
                </div>
                <div v-if="section.components.length === 0" class="empty-section">
                  <p>Drop components here</p>
                  <div class="drop-icon">⬇️</div>
                </div>
                <div class="section-components">
                  <div v-for="(comp, index) in section.components" :key="comp.name" class="component-item">
                    <div class="component-wrapper">
                      <button @click="removeFromSection(section.id, index)" class="hover-remove-btn" title="Remove component">✕</button>
                      <component 
                        :is="comp.component" 
                        :pdf-print-config="pdfPrintConfig"
                        :key="comp.name"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Default Drop Zone (no layout) -->
          <div v-else>
            <div v-if="loadedComponents.length === 0 && !isLoading" class="empty-drop-zone">
              <div class="drop-message">
                <h3>📋 Drag & Drop</h3>
                <p>Drag layouts to create sections, or drag components directly</p>
                <div class="drop-icon">⬇️</div>
              </div>
            </div>

            <!-- Loading Progress -->
            <div v-if="isLoading" class="loading-progress">
              <h3>Loading Component...</h3>
              <div class="progress-info">
                <p>Loading: {{ currentlyLoading || 'Preparing...' }}</p>
                <div class="loading-spinner">⏳</div>
              </div>
            </div>
            
            <!-- Error Display -->
            <div v-if="errors.length > 0" class="error-section">
              <h3>Loading Errors</h3>
              <div v-for="error in errors" :key="error.component" class="error-item">
                <strong>{{ formatComponentName(error.component) }}:</strong> {{ error.message }}
              </div>
            </div>
            
            <!-- Loaded Components Display (no layout) -->
            <div class="components-list">
              <div v-for="(loadedComp, index) in loadedComponents" :key="loadedComp.name" class="component-item">
                <div class="component-wrapper">
                  <button @click="removeComponent(index)" class="hover-remove-btn" title="Remove component">✕</button>
                  <component 
                    :is="loadedComp.component" 
                    :pdf-print-config="pdfPrintConfig"
                    :key="loadedComp.name"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useRoute } from 'vue-router';
import { ref, onMounted, computed } from 'vue';

import "@pdf/core";
import Utils from "../utils"
import EnvLauncher from '../mixins/EnvLauncher'

export default {
  name: 'MultiComponentLoader',
  mixins: [EnvLauncher],
  components: {
  },
  setup() {
    const route = useRoute();
    const shareClassId = route?.query?.shareClassId;
    const securityType = route?.query?.securityType;
    const fundServCode = route?.query?.fundServCode;
    const pdfOrientations = Utils.pdfOrientations;
    const pdfFormats = Utils.pdfFormats;
    const version = ref("");
    
    // Component selection and loading state
    const selectedComponents = ref([]);
    const loadedComponents = ref([]);
    const isLoading = ref(false);
    const currentlyLoading = ref(null);
    const errors = ref([]);
    
    // Drag and drop state
    const isDragOver = ref(false);
    const draggedComponent = ref(null);
    
    // Sidebar collapse state
    const isSidebarCollapsed = ref(false);
    
    // Group collapse states
    const groupStates = ref({
      components: true,
      layouts: true
    });
    
    // Layout state
    const currentLayout = ref(null);
    const layoutSections = ref([]);
    
    const pdfPrintConfig = ref({
      pdfHeaderText: undefined,
      pdfFooterText: undefined,
      pdfOrientation: "portrait",
      pdfFormat: "Letter",
    });

    // Available components list
    const availableComponentsList = [
      'pdf-esg-app',
      'pdf-esg-header', 
      'pdf-esg-footer',
      'pdf-esg-risk',
      'pdf-esg-risk-low-carbon',
      'pdf-esg-risk-rating',
      'pdf-esg-risk-rating-assessment',
      'pdf-esg-score-distribution',
      'pdf-esg-distribution-graph',
      'pdf-esg-controversy-level-distribution',
      'pdf-esg-corporate-holdings',
      'pdf-esg-corporate-holdings-analysis',
      'pdf-esg-product-involvement',
      'pdf-esg-fossil-fuel-involvement',
      'pdf-esg-carbon-designation',
      'pdf-esg-sustainability-capsule',
      'pdf-esg-values',
      'pdf-esg-values-chart',
      'pdf-eqsv-key-metrics',
      'pdf-eqsv-sustainability'
    ];

    // Available layouts list
    const availableLayoutsList = [
      {
        id: 'single-column',
        name: 'Single Column',
        description: 'Stack components vertically',
        icon: '📋',
        sections: [
          { id: 'main', name: 'Main Section', width: '100%', components: [] }
        ]
      },
      {
        id: 'two-column',
        name: 'Two Column',
        description: 'Side-by-side layout',
        icon: '📄',
        sections: [
          { id: 'left', name: 'Left Column', width: '50%', components: [] },
          { id: 'right', name: 'Right Column', width: '50%', components: [] }
        ]
      }
    ];

    // Progress calculation
    const progressPercentage = computed(() => {
      if (selectedComponents.value.length === 0) return 0;
      return Math.round((loadedComponents.value.length / selectedComponents.value.length) * 100);
    });

    // Dynamic component configuration generator (same as dynamic-component-renderer)
    const generateComponentConfig = (componentName) => {
      if (!componentName) return null;
      
      // Generate display name from component name
      const generateDisplayName = (name) => {
        return name
          .replace('pdf-', '')
          .replace(/-/g, ' ')
          .replace(/\b\w/g, l => l.toUpperCase())
          .replace(/^Esg/, 'ESG')
          .replace(/Eqsv/, 'EQSV');
      };
      
      // Determine config type based on component name
      const getConfigType = (name) => {
        if (name.startsWith('pdf-eqsv-')) return 'eqsv';
        if (name.startsWith('pdf-esg-')) return 'esg';
        if (name.startsWith('pdf-equity-')) return 'equity';
        if (name.startsWith('pdf-fund-')) return 'fund';
        return 'esg';
      };
      
      // Generate NPM package name from component name
      const generateNpmPackage = (name) => {
        return `@pdf/${name.replace('pdf-', '')}`;
      };
      
      return {
        name: generateDisplayName(componentName),
        value: componentName,
        cdnPath: `${componentName}.js`,
        npmPackage: generateNpmPackage(componentName),
        windowGlobal: componentName,
        configType: getConfigType(componentName)
      };
    };

    // Format component name for display
    const formatComponentName = (componentName) => {
      return componentName
        .replace('pdf-', '')
        .replace(/-/g, ' ')
        .replace(/\b\w/g, l => l.toUpperCase())
        .replace(/^Esg/, 'ESG')
        .replace(/Eqsv/, 'EQSV');
    };

    // Dynamic application config setup
    const setupApplicationConfig = (componentConfig) => {
      const applicationConfig = {
        integration: {
          id: "PDF-DEMO-APP",
        },
        investment: {
          id: shareClassId,
          type: securityType,
          marketUserGroup: "",
          fundServCode,
        },
        labels: {},
        components: {
          SalBasicPage: {
            type: `@pdf/${componentConfig.configType}`,
            settings: {},
            labels: {},
          },
        },
      };
      window.sal.configuration.setAppConfig(applicationConfig);
    };

    // Load a single component
    const loadSingleComponent = async (componentName) => {
      const componentConfig = generateComponentConfig(componentName);
      if (!componentConfig) {
        throw new Error(`Invalid component name: ${componentName}`);
      }

      // Setup application config for the component
      setupApplicationConfig(componentConfig);
      
      const host = `${window.location.protocol}//${window.location.host}`;
      let cdnUrl = `${host}/mf3/latest/${componentConfig.cdnPath}`;

      if (host.includes("pdfportal-stg")) {
        const pathName = window.location.pathname;
        const match = pathName.match(/(\d+\.)(\d+\.)(\d+)/);
        cdnUrl = match
          ? `${host}/mf3/v${match[0]}/${componentConfig.cdnPath}`
          : `${host}/mf3/latestRC/${componentConfig.cdnPath}`;
      }
      if (host.includes("pdfportal-uat")) {
        cdnUrl = `${host}/mf3/latestRC/${componentConfig.cdnPath}`;
      }
      if (host.includes("pdfportal-dev") || host.includes("pdfportal-qa")) {
        cdnUrl = `${host}/mf3/latestCI/${componentConfig.cdnPath}`;
      }
      if (host.includes("localhost")) {
        cdnUrl = `https://pdfportal-qa.psp98d75.easn.morningstar.com/mf3/latestCI/${componentConfig.cdnPath}`;
      }

      try {
        // Load CDN first - make Vue available globally for UMD build
        const vue = await import('vue');
        window.Vue = vue;
        await Utils.salImport(cdnUrl);

        if (window[componentConfig.windowGlobal]) {
          const component = window[componentConfig.windowGlobal].default || window[componentConfig.windowGlobal];
          if (component) {
            return component;
          }
        }

        throw new Error("CDN component not available");

      } catch (cdnError) {
        // Fallback to node_modules
        try {
          const moduleImport = await import(componentConfig.npmPackage);
          const component = moduleImport.default || moduleImport;

          if (component) {
            return component;
          } else {
            throw new Error("Failed to load component from both CDN and node_modules");
          }
        } catch (fallbackError) {
          throw new Error(`Failed to load ${componentName}: ${fallbackError.message}`);
        }
      }
    };

    // Load all selected components sequentially
    const loadSelectedComponents = async () => {
      if (selectedComponents.value.length === 0) return;
      
      isLoading.value = true;
      currentlyLoading.value = null;
      loadedComponents.value = [];
      errors.value = [];

      for (const componentName of selectedComponents.value) {
        try {
          currentlyLoading.value = formatComponentName(componentName);
          const component = await loadSingleComponent(componentName);
          
          loadedComponents.value.push({
            name: componentName,
            component: component
          });
          
        } catch (error) {
          console.error(`Failed to load ${componentName}:`, error);
          errors.value.push({
            component: componentName,
            message: error.message
          });
        }
      }
      
      isLoading.value = false;
      currentlyLoading.value = null;
    };

    // Clear all loaded components
    const clearAll = () => {
      loadedComponents.value = [];
      selectedComponents.value = [];
      errors.value = [];
    };

    // Remove a specific component
    const removeComponent = (index) => {
      loadedComponents.value.splice(index, 1);
    };

    // Toggle sidebar groups
    const toggleGroup = (groupName) => {
      groupStates.value[groupName] = !groupStates.value[groupName];
    };

    // Drag and drop handlers
    const onDragStart = (event, item, type) => {
      draggedComponent.value = { item, type };
      event.dataTransfer.setData('text/plain', JSON.stringify({ item, type }));
      event.dataTransfer.effectAllowed = 'copy';
    };

    const onDragEnd = () => {
      draggedComponent.value = null;
    };

    const onDragEnter = (event) => {
      event.preventDefault();
      isDragOver.value = true;
    };

    const onDragOver = (event) => {
      event.preventDefault();
      event.dataTransfer.dropEffect = 'copy';
    };

    const onDragLeave = (event) => {
      // Only set to false if leaving the drop zone completely
      if (!event.currentTarget.contains(event.relatedTarget)) {
        isDragOver.value = false;
      }
    };

    const onDrop = async (event) => {
      event.preventDefault();
      isDragOver.value = false;
      
      try {
        const dragData = JSON.parse(event.dataTransfer.getData('text/plain'));
        const { item, type } = dragData;
        
        if (!item || isLoading.value) return;
        
        if (type === 'layout') {
          // Handle layout drop - create sections
          const layout = item;
          currentLayout.value = layout;
          layoutSections.value = layout.sections.map(section => ({
            ...section,
            components: [],
            isDragOver: false
          }));
          // Clear any existing non-layout components
          loadedComponents.value = [];
          errors.value = [];
        } else if (type === 'component') {
          // Handle component drop to main area (only if no layout)
          if (!currentLayout.value) {
            const alreadyLoaded = loadedComponents.value.some(comp => comp.name === item);
            if (alreadyLoaded) return;
            
            try {
              isLoading.value = true;
              currentlyLoading.value = formatComponentName(item);
              
              const component = await loadSingleComponent(item);
              loadedComponents.value.push({
                name: item,
                component: component
              });
              
              errors.value = errors.value.filter(error => error.component !== item);
              
            } catch (error) {
              console.error(`Failed to load ${item}:`, error);
              errors.value = errors.value.filter(error => error.component !== item);
              errors.value.push({
                component: item,
                message: error.message
              });
            } finally {
              isLoading.value = false;
              currentlyLoading.value = null;
            }
          }
        }
      } catch (error) {
        console.error('Failed to parse drag data:', error);
      }
    };

    // Toggle sidebar collapse
    const toggleSidebar = () => {
      isSidebarCollapsed.value = !isSidebarCollapsed.value;
    };

    // Section drop handlers
    const onSectionDragEnter = (event, sectionId) => {
      event.preventDefault();
      const section = layoutSections.value.find(s => s.id === sectionId);
      if (section) section.isDragOver = true;
    };

    const onSectionDragOver = (event, sectionId) => {
      event.preventDefault();
      event.dataTransfer.dropEffect = 'copy';
    };

    const onSectionDragLeave = (event, sectionId) => {
      if (!event.currentTarget.contains(event.relatedTarget)) {
        const section = layoutSections.value.find(s => s.id === sectionId);
        if (section) section.isDragOver = false;
      }
    };

    const onSectionDrop = async (event, sectionId) => {
      event.preventDefault();
      const section = layoutSections.value.find(s => s.id === sectionId);
      if (section) section.isDragOver = false;
      
      try {
        const dragData = JSON.parse(event.dataTransfer.getData('text/plain'));
        const { item, type } = dragData;
        
        if (type === 'component' && !isLoading.value) {
          // Check if component is already in this section
          const alreadyInSection = section.components.some(comp => comp.name === item);
          if (alreadyInSection) return;
          
          try {
            isLoading.value = true;
            currentlyLoading.value = formatComponentName(item);
            
            const component = await loadSingleComponent(item);
            section.components.push({
              name: item,
              component: component
            });
            
            errors.value = errors.value.filter(error => error.component !== item);
            
          } catch (error) {
            console.error(`Failed to load ${item}:`, error);
            errors.value = errors.value.filter(error => error.component !== item);
            errors.value.push({
              component: item,
              message: error.message
            });
          } finally {
            isLoading.value = false;
            currentlyLoading.value = null;
          }
        }
      } catch (error) {
        console.error('Failed to parse drag data:', error);
      }
    };

    // Layout management
    const clearLayout = () => {
      currentLayout.value = null;
      layoutSections.value = [];
      errors.value = [];
    };

    const removeFromSection = (sectionId, componentIndex) => {
      const section = layoutSections.value.find(s => s.id === sectionId);
      if (section) {
        section.components.splice(componentIndex, 1);
      }
    };

    return {
      shareClassId,
      pdfOrientations,
      pdfPrintConfig,
      pdfFormats,
      availableComponentsList,
      selectedComponents,
      loadedComponents,
      isLoading,
      currentlyLoading,
      errors,
      progressPercentage,
      isDragOver,
      draggedComponent,
      formatComponentName,
      loadSelectedComponents,
      clearAll,
      removeComponent,
      onDragStart,
      onDragEnd,
      onDragEnter,
      onDragOver,
      onDragLeave,
      onDrop,
      isSidebarCollapsed,
      toggleSidebar,
      groupStates,
      toggleGroup,
      availableLayoutsList,
      currentLayout,
      layoutSections,
      onSectionDragEnter,
      onSectionDragOver,
      onSectionDragLeave,
      onSectionDrop,
      clearLayout,
      removeFromSection,
      version
    }
  }
}
</script>

<style scoped>
/* Drag and Drop Layout */
.drag-drop-layout {
  display: flex;
  height: calc(100vh - 100px);
  margin: 20px;
  gap: 20px;
}

/* Sidebar Styles */
.sidebar {
  width: 300px;
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 20px;
  overflow-y: auto;
  transition: all 0.3s ease;
}

.sidebar.collapsed {
  width: 60px;
  padding: 10px;
}

.sidebar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.sidebar-toggle {
  background: none;
  border: none;
  font-size: 16px;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.sidebar-toggle:hover {
  background-color: #e9ecef;
}

.sidebar h3 {
  margin: 0;
  color: #333;
  font-family: morningstarintrinsic, sans-serif;
}

.sidebar.collapsed .sidebar-content {
  display: none;
}

/* Sidebar Groups */
.sidebar-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.sidebar-group {
  background-color: white;
  border-radius: 6px;
  overflow: hidden;
}

.group-header {
  display: flex;
  align-items: center;
  padding: 12px;
  background-color: #e9ecef;
  cursor: pointer;
  transition: background-color 0.2s;
  gap: 8px;
}

.group-header:hover {
  background-color: #dee2e6;
}

.group-header h4 {
  margin: 0;
  flex: 1;
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

.group-icon {
  font-size: 14px;
}

.toggle-icon {
  font-size: 12px;
  color: #666;
}

.group-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px;
}

/* Draggable Items */
.draggable-item {
  display: flex;
  align-items: flex-start;
  padding: 8px;
  background-color: #f8f9fa;
  border-radius: 4px;
  cursor: grab;
  transition: all 0.2s ease;
  border: 1px solid transparent;
  gap: 8px;
}

.draggable-item:hover {
  background-color: #e3f2fd;
  border-color: #1976d2;
}

.draggable-item:active {
  cursor: grabbing;
  transform: rotate(1deg);
}

.item-icon {
  font-size: 16px;
  flex-shrink: 0;
  margin-top: 2px;
}

.item-name {
  font-size: 13px;
  font-weight: 500;
  color: #333;
  line-height: 1.2;
  flex: 1;
}

.layout-item {
  flex-direction: column;
  align-items: flex-start;
  padding: 10px;
}

.layout-item .item-icon {
  font-size: 20px;
  margin-bottom: 4px;
}

.layout-item .item-name {
  font-weight: 600;
  margin-bottom: 2px;
}

.item-description {
  font-size: 11px;
  color: #666;
  line-height: 1.2;
}

.draggable-components {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.draggable-component {
  display: flex;
  align-items: center;
  padding: 12px;
  background-color: white;
  border-radius: 6px;
  cursor: grab;
  transition: all 0.2s ease;
  border: 2px solid transparent;
}

.draggable-component:hover {
  background-color: #e3f2fd;
  border-color: #1976d2;
}

.draggable-component:active {
  cursor: grabbing;
  transform: rotate(2deg);
}

.component-icon {
  font-size: 18px;
  margin-right: 12px;
  flex-shrink: 0;
}

.component-name {
  font-size: 14px;
  font-family: morningstarintrinsic, sans-serif;
  font-weight: 500;
  color: #333;
  line-height: 1.2;
}

/* Main Area Styles */
.main-area {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.drop-zone {
  flex: 1;
  border: 3px dashed #ddd;
  border-radius: 8px;
  position: relative;
  transition: all 0.3s ease;
  overflow-y: auto;
}

.drop-zone.drag-over {
  border-color: #1976d2;
  background-color: #e3f2fd;
}

.drop-zone.has-components {
  border: none;
  background-color: transparent;
}

/* Empty Drop Zone */
.empty-drop-zone {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  text-align: center;
}

.drop-message {
  color: #666;
}

.drop-message h3 {
  margin-bottom: 12px;
  color: #333;
  font-family: morningstarintrinsic, sans-serif;
}

.drop-message p {
  margin-bottom: 16px;
  font-size: 14px;
  font-family: morningstarintrinsic, sans-serif;
}

.drop-icon {
  font-size: 24px;
  opacity: 0.5;
}

/* Loading and Error Styles */
.loading-progress {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  text-align: center;
}

.loading-spinner {
  font-size: 24px;
  margin-top: 12px;
}

.error-section {
  padding: 16px;
  margin: 16px;
  background-color: #ffebee;
  border-radius: 6px;
}

.error-item {
  margin-bottom: 8px;
  padding: 8px;
  background-color: white;
  border-radius: 4px;
}

/* Components List */
.components-list {
  padding: 20px;
}

.component-item {
  margin-bottom: 40px;
  padding: 20px 0;
}

.component-wrapper {
  position: relative;
}

.hover-remove-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  background-color: #dc3545;
  color: white;
  border: none;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  cursor: pointer;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s ease;
  z-index: 10;
}

.component-wrapper:hover .hover-remove-btn {
  opacity: 1;
}

.hover-remove-btn:hover {
  background-color: #c82333;
  transform: scale(1.1);
}

/* Clear button */
.clear-button {
  padding: 8px 16px;
  border: none;
  cursor: pointer;
  background-color: #dc3545;
  color: white;
  border-radius: 4px;
}

.clear-button:hover:not(:disabled) {
  background-color: #c82333;
}

.clear-button:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
}

/* Layout Container */
.layout-container {
  padding: 20px;
  height: 100%;
}

.layout-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 2px solid #e9ecef;
}

.layout-header h3 {
  margin: 0;
  color: #333;
  font-size: 18px;
}

.clear-layout-btn {
  padding: 6px 12px;
  background-color: #6c757d;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
}

.clear-layout-btn:hover {
  background-color: #5a6268;
}

/* Sections Container */
.sections-container {
  display: flex;
  gap: 20px;
  height: calc(100% - 80px);
}

.sections-container.single-column {
  flex-direction: column;
}

.sections-container.two-column {
  flex-direction: row;
}

/* Layout Section */
.layout-section {
  border: 2px dashed #ddd;
  border-radius: 8px;
  padding: 16px;
  transition: all 0.3s ease;
  min-height: 200px;
  display: flex;
  flex-direction: column;
}

.layout-section.drag-over {
  border-color: #1976d2;
  background-color: #e3f2fd;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e9ecef;
}

.section-header h4 {
  margin: 0;
  color: #333;
  font-size: 14px;
  font-weight: 600;
}

.component-count {
  font-size: 12px;
  color: #666;
  background-color: #f8f9fa;
  padding: 2px 6px;
  border-radius: 10px;
}

/* Empty Section */
.empty-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: #666;
}

.empty-section p {
  margin-bottom: 8px;
  font-size: 14px;
}

.empty-section .drop-icon {
  font-size: 24px;
  opacity: 0.5;
}

/* Section Components */
.section-components {
  flex: 1;
  overflow-y: auto;
}

.section-components .component-item {
  margin-bottom: 16px;
}
</style>